// ============================================
// CONSTANTES FISCALES 2026 — PSYCHOLOGUES CIPAV
// Sources : Guide ARAPL 2025, Service-Public.fr (01/01/2026),
// URSSAF.fr, CIPAV.fr, economie.gouv.fr, mars 2026
// ============================================

// TAUX MICRO-BNC CIPAV (psychologues — profession réglementée)
export const TAUX_COTISATIONS_MICRO_CIPAV = 0.232   // 23,2% du CA
export const TAUX_CFP_CIPAV = 0.002                 // 0,2% du CA (formation pro)
export const TAUX_TOTAL_MICRO_CIPAV = 0.234         // 23,4% avec CFP
export const TAUX_VERSEMENT_LIBERATOIRE = 0.254     // 23,2% + 2,2% IR (si éligible)

// TAUX BNC CLASSIQUE (estimation — taux réel calculé par URSSAF)
export const TAUX_COTISATIONS_BNC_CLASSIQUE = 0.40  // ~40% du bénéfice net

// IR
export const ABATTEMENT_MICRO_BNC = 0.34            // 34%
export const TAUX_REVENU_IMPOSABLE_MICRO = 0.66     // 66% du CA

// SEUILS 2026-2028 (Loi de Finances 2026 — réévalués triennalement)
export const SEUIL_MICRO_BNC = 83600                // Seuil Micro-BNC 2026-2028 — 83 600€ (ex 77 700€)
export const SEUIL_TVA_FRANCHISE = 37500            // Nouveau seuil 2026
export const SEUIL_TVA_MAJORE = 41250               // Nouveau seuil 2026
export const PASS_2026 = 48060

// EXONÉRATION TVA — Soins psychologiques
// Les prestations de soins psychologiques (diagnostics et traitements)
// sont exonérées de TVA sans limite de seuil pour les titulaires des
// diplômes requis (art. 261-4-1° du CGI).
// La TVA ne s'applique qu'aux activités hors soins : expertise, formation, conseil.
export const TVA_EXONEREE_SOINS = true
export const ACTIVITES_SOUMISES_TVA = ["formation", "expertise", "conseil", "supervision externe"]

// BARÈME IR 2026 — vérifié service-public.fr
export const BAREME_IR_2026 = [
  { min: 0,      max: 11600,   taux: 0    },
  { min: 11600,  max: 29579,   taux: 0.11 },
  { min: 29579,  max: 84577,   taux: 0.30 },
  { min: 84577,  max: 181917,  taux: 0.41 },
  { min: 181917, max: Infinity, taux: 0.45 },
]

// MENTIONS LÉGALES ARAPL
export const DISCLAIMER_ARAPL = "Estimation basée sur le guide ARAPL 2025 et les taux CIPAV 2026. Membre d'un organisme agréé, le règlement par chèque ou carte bancaire est accepté."
export const DISCLAIMER_EXPERT = "Ces estimations sont indicatives et ne remplacent pas les conseils d'un expert-comptable."

// ============================================
// FONCTIONS DE CALCUL
// ============================================

export const calculIR = (revenuImposable: number, parts = 1): number => {
  if (revenuImposable <= 0) return 0
  const revenuParPart = revenuImposable / parts
  let irParPart = 0
  for (const tranche of BAREME_IR_2026) {
    if (revenuParPart <= tranche.min) break
    irParPart += (Math.min(revenuParPart, tranche.max) - tranche.min) * tranche.taux
  }
  // Math.floor : conforme au calcul DGFIP (troncature, non arrondi)
  return Math.floor(irParPart * parts)
}

// Cotisations sociales Micro-BNC CIPAV (sans CFP)
export const calculCotisations_micro = (ca: number): number =>
  Math.round(ca * TAUX_COTISATIONS_MICRO_CIPAV)

// Alias rétrocompatibilité
export const calculURSSAF_micro = calculCotisations_micro

// Cotisations + CFP
export const calculCotisationsTotal_micro = (ca: number): number =>
  Math.round(ca * TAUX_TOTAL_MICRO_CIPAV)

// BNC classique
export const calculURSSAF_bnc = (beneficeNet: number): number =>
  Math.round(beneficeNet * TAUX_COTISATIONS_BNC_CLASSIQUE)

// Bénéfice imposable Micro-BNC
export const calculBeneficeMicro = (ca: number): number =>
  Math.round(ca * TAUX_REVENU_IMPOSABLE_MICRO)

// Provision à mettre de côté (Micro-BNC) — décomposée
export const calculProvisionSociale = (ca: number): number =>
  Math.round(ca * TAUX_TOTAL_MICRO_CIPAV)

export const calculProvisionFiscale = (ca: number, parts = 1): number =>
  calculIR(calculBeneficeMicro(ca), parts)

export const calculProvisionTotale = (ca: number, parts = 1): number =>
  calculProvisionSociale(ca) + calculProvisionFiscale(ca, parts)

// Revenu net Micro-BNC : CA - Social - IR
export const calculRevenuNetMicro = (ca: number, parts = 1): number => {
  const cotisations = calculCotisations_micro(ca)
  const ir = calculIR(calculBeneficeMicro(ca), parts)
  return Math.round(ca - cotisations - ir)
}

// Alias rétrocompatibilité
export const calculRevenüNet_micro = calculRevenuNetMicro

// Revenu net BNC classique
export const calculRevenüNet_bnc = (
  ca: number,
  charges: number,
  parts = 1
): number => {
  if (charges <= 0) return 0
  const beneficeNet = Math.max(0, ca - charges)
  const urssaf = calculURSSAF_bnc(beneficeNet)
  const imposable = Math.max(0, beneficeNet - urssaf)
  const ir = calculIR(imposable, parts)
  return Math.round(ca - charges - urssaf - ir)
}

// Taux horaire net réel
export const calculTauxHoraire = (
  ca: number,
  nombreSeances: number,
  dureeHeures = 1
): number => {
  if (nombreSeances <= 0 || dureeHeures <= 0) return 0
  const net = calculRevenuNetMicro(ca)
  return Math.round(net / (nombreSeances * dureeHeures))
}

// Vérification seuils
export const verifierSeuilMicroBNC = (ca: number) => ({
  depasse: ca > SEUIL_MICRO_BNC,
  ecart: Math.max(0, SEUIL_MICRO_BNC - ca),
  pourcentage: Math.round((ca / SEUIL_MICRO_BNC) * 100)
})

export const verifierSeuilTVA = (ca: number) => ({
  depasse: ca > SEUIL_TVA_FRANCHISE,
  ecart: Math.abs(SEUIL_TVA_FRANCHISE - ca),
  pourcentage: Math.round((ca / SEUIL_TVA_FRANCHISE) * 100)
})

// Vérifie si des recettes sont soumises à TVA (hors soins)
export const hasRecettesSoumisesTVA = (recettes: { description?: string | null }[]): boolean => {
  return recettes.some(r => {
    const desc = (r.description || "").toLowerCase()
    return ACTIVITES_SOUMISES_TVA.some(a => desc.includes(a))
  })
}

// CA des recettes soumises à TVA uniquement
export const calculCASoumiseTVA = (recettes: { montant: number; description?: string | null }[]): number => {
  return recettes
    .filter(r => {
      const desc = (r.description || "").toLowerCase()
      return ACTIVITES_SOUMISES_TVA.some(a => desc.includes(a))
    })
    .reduce((s, r) => s + Number(r.montant), 0)
}

// Économie BNC classique vs Micro
export const calculEconomieBNC = (
  ca: number,
  chargesReelles: number
): number => {
  if (chargesReelles <= 0) return 0
  const netMicro = calculRevenuNetMicro(ca)
  const netBNC = calculRevenüNet_bnc(ca, chargesReelles)
  return netBNC - netMicro
}
